import { create } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import { UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'

import {
  AcceptEmailThreadAddressSuggestionRequestSchema,
  DismissEmailThreadAddressSuggestionRequestSchema,
} from '@/gen/cortado/v1/email_conversations_pb'
import type { EmailThreadAddressSuggestion } from '@/gen/cortado/v1/email_conversations_pb'
import { Button } from '@/components/ui/button'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { usePermissions } from '@/hooks/use-permissions'
import {
  useAcceptEmailThreadAddressSuggestion,
  useDismissEmailThreadAddressSuggestion,
  useEmailThreadAddressSuggestions,
} from '@/stores/email-address-suggestions.store'

const ADD_FAILED_MESSAGE = "Couldn't add the address. Try again."
const DISMISS_FAILED_MESSAGE = "Couldn't dismiss the suggestion. Try again."

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ConnectError && err.rawMessage ? err.rawMessage : fallback
}

interface ThreadAddressSuggestionsProps {
  organizationId: string
  threadId: string
}

/**
 * "Add jane.work@… to Jane Doe?" chips for a conversation (CPRO-788). Each
 * offers an address sent under a linked contact's name that no contact has
 * yet. Adding is one click and never automatic; dismissing hides it for the
 * current user only. Needs Manage Contacts, since adding changes the contact.
 */
export function ThreadAddressSuggestions({ organizationId, threadId }: ThreadAddressSuggestionsProps) {
  const { hasFeature } = useHasEmailInboxFeature()
  const { can } = usePermissions(organizationId)
  const canManageContacts = can('ContactsManage')
  const { suggestions } = useEmailThreadAddressSuggestions(organizationId, threadId, {
    enabled: hasFeature && canManageContacts,
  })
  const accept = useAcceptEmailThreadAddressSuggestion()
  const dismiss = useDismissEmailThreadAddressSuggestion()

  if (!hasFeature || !canManageContacts || suggestions.length === 0) return null

  const busy = accept.isPending || dismiss.isPending

  const handleAccept = async (s: EmailThreadAddressSuggestion) => {
    try {
      await accept.mutateAsync(
        create(AcceptEmailThreadAddressSuggestionRequestSchema, {
          organizationId,
          threadId,
          contactId: s.contactId,
          email: s.email,
        }),
      )
      toast.success(`Added ${s.email} to ${s.contactName}`)
    } catch (err) {
      toast.error(errorMessage(err, ADD_FAILED_MESSAGE))
    }
  }

  const handleDismiss = async (s: EmailThreadAddressSuggestion) => {
    try {
      await dismiss.mutateAsync(
        create(DismissEmailThreadAddressSuggestionRequestSchema, { organizationId, threadId, email: s.email }),
      )
    } catch (err) {
      toast.error(errorMessage(err, DISMISS_FAILED_MESSAGE))
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {suggestions.map((s) => (
        <span
          key={`${s.contactId}:${s.email}`}
          className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs"
        >
          <span>
            Add <span className="font-medium">{s.email}</span> to {s.contactName}?
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-xs"
            onClick={() => void handleAccept(s)}
            disabled={busy}
          >
            <UserPlus className="h-3 w-3" />
            Add
          </Button>
          <button
            type="button"
            onClick={() => void handleDismiss(s)}
            disabled={busy}
            aria-label={`Dismiss suggestion for ${s.email}`}
            title="Dismiss"
            className="rounded-full p-0.5 hover:bg-muted-foreground/20 disabled:opacity-50"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  )
}
